import useAxios from '../../hooks/useAxios'
import { AxiosError } from 'axios'
import { normalizeAxiosError } from '../../reducers/errorReducer'
import { DriveItem } from '@microsoft/microsoft-graph-types'
import useQueueStateHandler, { QueueStateTransition, QueueStatus, QueueStatusData } from '../useQueueStateHandler'
import { Queue } from '../../context/QueueContext'
import DocumentToastContent from '../../components/Toast/DocumentToastContent'
import * as React from 'react'

export enum DocumentGenerationType {
    BilledTurnover = 'billed-turnover',
    CostCentre = 'cost-centre',
    CRMFileXls = 'crm-file-xls',
    FileCard = 'file-card',
    FileCreation = 'file-creation',
    FileClaimListXls = 'file-claim-list-xls',
    FileXls = 'file-xls',
    FixedPriceXls = 'fixed-price-xls',
    Invoice = 'invoice',
    InvoiceXls = 'invoice-xls',
    Office = 'office',
    OfficeV2 = 'office-v2',
    OngoingFiles = 'ongoing-files',
    OutstandingCharges = 'outstanding-charges',
    OutstandingInvoices = 'outstanding-invoices',
    PartyCard = 'party-card',
    PartyXls = 'party-xls',
    PdfConversion = 'pdf-conversion',
    RatesPerFile = 'rates-per-file',
    TaskXls = 'task-xls',
    TimeXls = 'time-xls',
    TurnoverDashboardXls = 'turnover-dashboard-xls',
    UserProductivity = 'user-productivity',
    WipPosition = 'wip-position',
    Zip = 'zip',
}

// The data that applies to the whole chain of events.
export type DocumentGenerationContextData = {
    generationType: DocumentGenerationType
}

export type DocumentGenerationParams<ParamsT> = ParamsT & { generationType: DocumentGenerationType }

// The data that is specific for document generation for a given status transition and will not persist through the
// whole chain of events. Contains the drive item in which the document is stored and the message ID from Azure Service
// Bus which will be provided to us by the server.
export type DocumentGenerationStatusData = {
    messageId?: string
    driveItem?: DriveItem
}

export default function useDocumentGenerationHandler<RequestT = {}, StatusT = {}>() {
    const axios = useAxios()

    const cancel = (
        queue: Queue,
        transition: QueueStateTransition<StatusT>,
        requestId: string,
        graceful: boolean,
        status?: QueueStatusData<DocumentGenerationStatusData>,
    ) => {
        if (!status?.messageId) {
            // Directly remove from the queue if we're not waiting for a delete request to finish.
            queue.remove(requestId)
        } else {
            axios
                .delete(`document/queue/${status.messageId}`, { headers: { SuppressErrors: true } })
                .catch((response: AxiosError) => {
                    // Don't handle anything that is returned by the server, if we're already restarting and not
                    // interest in status updates of the old attempt.
                    if (graceful) {
                        const error = normalizeAxiosError(response)
                        transition(requestId, QueueStatus.Failed, {
                            error: {
                                message: error?.message,
                                trace: error?.trace,
                            },
                        })
                    }
                })
                .finally(() => graceful && queue.remove(requestId))
        }
    }

    return useQueueStateHandler<RequestT, StatusT, DocumentGenerationContextData>(props => <DocumentToastContent {...props} />, cancel)
}
